import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { DollarSign, ShoppingBag, TrendingUp, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLORS = ["#4F46E5", "#059669", "#D97706", "#DB2777", "#0891B2", "#7C3AED"];
const RANGES = [{ k: "today", label: "Today" }, { k: "week", label: "This Week" }, { k: "month", label: "This Month" }, { k: "all", label: "All Time" }];
const hourLabel = (h) => (h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [range, setRange] = useState("today");
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setD(null); setErr(false);
    api.get(`/analytics/dashboard?range=${range}`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, [range, reloadKey]);

  const RangeBar = (
    <div className="flex flex-wrap items-center gap-2" data-testid="analytics-range-bar">
      {RANGES.map((r) => (
        <Button key={r.k} size="sm" variant={range === r.k ? "default" : "outline"} onClick={() => setRange(r.k)} data-testid={`analytics-range-${r.k}`}>{r.label}</Button>
      ))}
    </div>
  );

  if (err) return <div className="space-y-6 max-w-7xl">{RangeBar}<div className="text-red-600 text-sm" data-testid="analytics-error">Couldn't load analytics. <button className="underline" data-testid="analytics-retry" onClick={() => setReloadKey((k) => k + 1)}>Retry</button></div></div>;
  if (!d) return <div className="space-y-6 max-w-7xl">{RangeBar}<div className="text-slate-400 text-sm" data-testid="analytics-loading">Loading analytics…</div></div>;

  const hasSales = d.transaction_count > 0;

  const stats = [
    { label: "Total Revenue", value: money(d.total_revenue), icon: DollarSign, color: "text-emerald-600 bg-emerald-50" },
    { label: "Transactions", value: d.transaction_count, icon: ShoppingBag, color: "text-indigo-600 bg-indigo-50" },
    { label: "Avg Transaction", value: money(d.avg_transaction), icon: TrendingUp, color: "text-blue-600 bg-blue-50" },
    { label: "Gross Profit", value: money(d.gross_profit), icon: DollarSign, color: "text-purple-600 bg-purple-50" },
    { label: "Prize Value Given", value: money(d.prize_value_given), icon: Gift, color: "text-amber-600 bg-amber-50" },
  ];

  return (
    <div className="space-y-6 max-w-7xl">
      {RangeBar}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-4" data-testid={`stat-${s.label.replace(/\s/g, "-").toLowerCase()}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${s.color}`}><s.icon className="w-5 h-5" /></div>
            <div className="text-2xl font-bold text-slate-900">{s.value}</div>
            <div className="text-xs text-slate-500">{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Sales by Hour</h3>
          {hasSales ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={d.sales_by_hour}>
                <XAxis dataKey="hour" tickFormatter={hourLabel} tick={{ fontSize: 11 }} interval={1} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => money(v)} labelFormatter={(h) => hourLabel(h)} /><Bar dataKey="value" fill="#4F46E5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400" data-testid="empty-sales-by-hour">No sales in this period</div>}
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Revenue by Payment</h3>
          {d.revenue_by_payment.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={d.revenue_by_payment} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={(e) => `${e.name} ${money(e.value)}`}>
                  {d.revenue_by_payment.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400" data-testid="empty-revenue-by-payment">No sales in this period</div>}
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Revenue by Category</h3>
          <div className="space-y-2" data-testid="revenue-by-category">
            {d.revenue_by_category.slice().sort((a, b) => b.value - a.value).map((c, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{c.name}</span><span className="font-semibold">{money(c.value)}</span></div>
            ))}
            {d.revenue_by_category.length === 0 && <div className="text-xs text-slate-400">No sales in this period</div>}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Top Products by Revenue</h3>
          <div className="space-y-2">
            {d.top_products_revenue.map((p, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{p.name}</span><span className="font-semibold">{money(p.value)}</span></div>
            ))}
            {d.top_products_revenue.length === 0 && <div className="text-xs text-slate-400">No sales in this period</div>}
          </div>
        </Card>
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Top Employees</h3>
          <div className="space-y-2">
            {d.top_employees.map((p, i) => (
              <div key={i} className="flex justify-between text-sm"><span className="text-slate-600">{p.name}</span><span className="font-semibold">{money(p.value)}</span></div>
            ))}
            {d.top_employees.length === 0 && <div className="text-xs text-slate-400">No sales in this period</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
